import { useEffect, useRef } from "react";
import type { VideoCanvasObject } from "@refinement-canvas/domain";
import { usesCrosshairCursor, useCanvasStore } from "../store/canvas-store";
import { useObjectDrag } from "../input/use-object-drag";
import { useCanvasOpDispatcherContext } from "../dispatcher-context";
import { isPendingAttachmentSrc } from "../attachment-placeholder";

interface Props {
  readonly object: VideoCanvasObject;
}

// 動画オブジェクト（MP4 / WebM 等）。
// ビューポート外に出た要素は IntersectionObserver で pause() し、CPU を節約する。
// クリックで選択、ドラッグで移動できる（コントロール欄からの再生・音量はそのまま使える）。
export function VideoObject({ object }: Props): JSX.Element {
  const ref = useRef<HTMLVideoElement>(null);
  const dispatcher = useCanvasOpDispatcherContext();
  const activeTool = useCanvasStore((s) => s.tool);
  // 単一選択時だけでなく、選択集合（Ctrl+クリックや範囲指定での複数選択を含む）に
  // 入っていればハイライトする。
  const isSelected = useCanvasStore((s) => s.selectedObjectIds.includes(object.id));

  useObjectDrag({
    objectId: object.id,
    elementRef: ref as unknown as React.RefObject<HTMLElement | null>,
    dispatcher,
    initialX: object.x,
    initialY: object.y,
    initialW: object.w,
    initialH: object.h,
    initialRotation: object.rotation,
    disabled: activeTool === "draw-pen",
  });

  // アップロード未完了の仮表示（src が data: URL のプレースホルダ）はローディング枠を重ねる。
  // 確定済みの blob:（本人）/ https URL では通常の動画として再生できる。
  const isPending = isPendingAttachmentSrc(object.src);

  // ビューポート外で自動 pause、再表示時は手動再生に任せる（自動再生は UX を阻害するため）。
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting && !el.paused) {
            el.pause();
          }
        }
      },
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <video
        ref={ref}
        data-testid={`canvas-object-${object.id}`}
        data-object-type="video"
        data-pending={isPending ? "true" : undefined}
        src={object.src}
        controls={!isPending}
        preload="metadata"
        style={{
          position: "absolute",
          left: `${object.x}px`,
          top: `${object.y}px`,
          width: `${object.w}px`,
          height: `${object.h}px`,
          zIndex: object.zOrder,
          background: "#000",
          transform: object.rotation ? `rotate(${object.rotation}deg)` : undefined,
          transformOrigin: "center",
          // 配置ツール・ペン描画の選択中は新規作成（crosshair）であることを示し、移動と誤認させない。
          cursor: usesCrosshairCursor(activeTool) ? "crosshair" : "move",
          pointerEvents: activeTool === "draw-pen" ? "none" : "auto",
          // 選択枠は FigJam 風の水色で、ズーム倍率に依らず画面上一定の太さ（≒2px）にする。
          outline: isSelected
            ? "calc(2px * var(--canvas-inv-zoom, 1)) solid var(--color-selection)"
            : "none",
        }}
      />
      {isPending && (
        <div
          className="attachment-pending-overlay"
          data-variant="loading"
          style={{
            position: "absolute",
            left: `${object.x}px`,
            top: `${object.y}px`,
            width: `${object.w}px`,
            height: `${object.h}px`,
            zIndex: object.zOrder,
            transform: object.rotation ? `rotate(${object.rotation}deg)` : undefined,
            transformOrigin: "center",
            pointerEvents: "none",
          }}
        >
          <span className="attachment-pending-spinner" aria-hidden="true" />
        </div>
      )}
    </>
  );
}
